import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
  InfoWindow,
  TransitLayer,
} from "@react-google-maps/api";
import { postDataWithBody } from "../../../Services/Services";
import { getWayPoints, fetchVehicleTrackingWithIdealPoints,fetchVehicleTrackingWithSummary } from "../../../Services/EndPoints";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Brush,
} from "recharts";

// ─── Shared Google Maps loader (fixes "Loader must not be called again" error) ─
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Map config ───────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 17.4435, lng: 78.3485 };

// ─── Layer options — inline SVGs matching RouteList/AssignRoute style ─────────
const LAYERS = [
  {
    id: "roadmap", label: "Standard",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

// ─── Layer control (matches GPS Tracking reference — same icon, position, style) ─
const CustomLayerControl = ({ mapType, setMapType }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: 40, height: 40, border: "none", background: "white",
          borderRadius: 8, cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: 45, right: 0,
          background: "white", borderRadius: 10, padding: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 160,
          border: "1px solid #e2e8f0",
        }}>
          {LAYERS.map((opt) => (
            <div
              key={opt.id}
              onClick={() => { setMapType(opt.id); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 9,
                padding: "8px 10px", cursor: "pointer", borderRadius: 7,
                background: mapType === opt.id ? "#f0fdfb" : "transparent",
                transition: "background 0.15s",
              }}
            >
              <span style={{ color: mapType === opt.id ? "#1a7a73" : "#94a3b8" }}>{opt.icon}</span>
              <span style={{
                fontSize: 13,
                fontWeight: mapType === opt.id ? 600 : 400,
                color: mapType === opt.id ? "#1a7a73" : "#334155",
              }}>{opt.label}</span>
              {mapType === opt.id && (
                <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Vehicle icon helpers (same as TripAction.js) ────────────────────────────
const _calcBearing = (from, to) => {
  const toRad = d => d * Math.PI / 180;
  const lat1 = toRad(from.lat), lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  return (Math.atan2(Math.sin(dLng) * Math.cos(lat2), Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)) * 180 / Math.PI + 360) % 360;
};

const TRAVEL_VEHICLE_EMOJI = {
  car: '🚗', sedan: '🚗', bus: '🚌', minibus: '🚌',
  truck: '🚛', lorry: '🚛', bike: '🏍️', bicycle: '🚲',
  motorcycle: '🏍️', motorbike: '🏍️', van: '🚐', suv: '🚙',
  autorickshaw: '🛺', auto: '🛺', rickshaw: '🛺',
  ambulance: '🚑', firetruck: '🚒', police: '🚔', taxi: '🚕', tractor: '🚜',
};

const getTravelVehicleIcon = (vehicleType, heading = 0) => {
  const type  = (vehicleType || '').toLowerCase().replace(/[\s_-]+/g, '');
  const emoji = TRAVEL_VEHICLE_EMOJI[type] || '🚘';
  const deg   = Math.round(heading);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
    <defs>
      <radialGradient id="g" cx="50%" cy="40%">
        <stop offset="0%" stop-color="#28a745"/>
        <stop offset="100%" stop-color="#1e7e34"/>
      </radialGradient>
      <filter id="sh" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="rgba(0,0,0,0.32)"/>
      </filter>
    </defs>
    <circle cx="26" cy="26" r="22" fill="url(#g)" stroke="white" stroke-width="3" filter="url(#sh)"/>
    <text x="26" y="31" font-size="20" text-anchor="middle"
          font-family="Segoe UI Emoji,Apple Color Emoji,Noto Color Emoji,sans-serif">${emoji}</text>
    <g transform="rotate(${deg},26,26)">
      <polygon points="26,4 30,13 26,11 22,13" fill="white" opacity="0.95"/>
    </g>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(52, 52),
    anchor: new window.google.maps.Point(26, 26),
  };
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// ─── LoadingOverlay ───────────────────────────────────────────────────────────
const LoadingOverlay = ({ message = "Loading..." }) => (
  <div style={{
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0, minHeight: "100vh",
    backgroundColor: "rgba(255,255,255,0.95)", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", zIndex: 999,
  }}>
    <div className="spinner-border text-primary mb-3" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
    <div style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>{message}</div>
  </div>
);

// ─── SpeedHistoryComponent ────────────────────────────────────────────────────
const SpeedHistoryComponent = ({ vehicleNumber, startDate, endDate, onDataLoad }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    const pad = (n) => String(n).padStart(2, "0");
    const ms = String(date.getMilliseconds()).padStart(3, "0");
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${ms}`
    );
  };

  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes(":")) {
      const parts = timeStr.split(":");
      if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  const transformDataForContinuous = (raw) =>
    raw.map((item) => ({
      ...item,
      speed: Number(item.speed),
      vehicle_tracking_time: item.vehicle_tracking_time.substr(11, 8),
    }));

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    if (!cx || !cy) return null;

    if (payload.event_type === "rollover") {
      return (
        <svg x={cx - 16} y={cy - 16} width={32} height={32} viewBox="0 0 64 64">
          <g transform="rotate(-90 32 32)">
            <rect x="14" y="28" width="36" height="10" rx="3" fill="#f57c00" stroke="#000" strokeWidth="2" />
            <rect x="20" y="22" width="24" height="8" rx="2" fill="#fb8c00" stroke="#000" strokeWidth="2" />
            <circle cx="22" cy="42" r="4" fill="black" /><circle cx="42" cy="42" r="4" fill="black" />
          </g>
          <line x1="8" y1="50" x2="56" y2="50" stroke="#555" strokeWidth="2" strokeDasharray="4 2" />
        </svg>
      );
    }
    if (payload.event_type === "impact") {
      return (
        <svg x={cx - 16} y={cy - 16} width={32} height={32} viewBox="0 0 64 64">
          <path d="M12 32 A20 20 0 0 1 52 32" fill="none" stroke="#ff4444" strokeWidth="3" strokeDasharray="6 4" />
          <g transform="translate(32 32)">
            <rect x="-18" y="-6" width="36" height="12" rx="4" fill="#e53935" stroke="#000" strokeWidth="2" />
            <rect x="-12" y="-12" width="24" height="8" rx="3" fill="#e53935" stroke="#000" strokeWidth="2" />
            <circle cx="-10" cy="10" r="4" fill="black" /><circle cx="10" cy="10" r="4" fill="black" />
          </g>
        </svg>
      );
    }
    if (payload.event_type === "sos") {
      return (
        <svg x={cx - 14} y={cy - 14} width={32} height={32} viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="26" fill="#dc3545" />
          <circle cx="32" cy="32" r="20" fill="#ff6b6b" />
          <text x="32" y="38" textAnchor="middle" fontSize="16" fontWeight="bold" fill="white">SOS</text>
        </svg>
      );
    }

    let fillColor = "#3CB371";
    if (payload.speed >= 40 && payload.speed <= 60) fillColor = "#FFA500";
    else if (payload.speed > 60) fillColor = "#FF0000";
    return <circle cx={cx} cy={cy} r={4} fill={fillColor} stroke="#fff" strokeWidth={1} />;
  };

  const formatTooltip = (value, name) => {
    if (name === "speed") {
      let status = "Safe Speed";
      if (value > 40 && value <= 60) status = "Slightly Above Threshold";
      if (value > 60) status = "Over-Speeding";
      return [`${value} km/h`, status];
    }
    return [value, name];
  };

  const isMounted = useRef(true);
  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);
  const fetchTriggered = useRef(false);

  useEffect(() => {
    if (!vehicleNumber || !startDate || !endDate) return;
    if (fetchTriggered.current) return;
    fetchTriggered.current = true;

    const fetchVehicleData = async () => {
      setIsLoading(true); setApiError("");
      try {
        const payload = { vehicleNumber, startTime: formatDateTime(startDate), endTime: formatDateTime(endDate) };
        const result = await postDataWithBody(fetchVehicleTrackingWithSummary, payload, {});
        if (isMounted.current) {
          setData(transformDataForContinuous(result.records));
          if (onDataLoad && typeof onDataLoad === "function") onDataLoad(result.summary);
        }
      } catch (error) {
        console.error("Failed to fetch vehicle data:", error);
        if (isMounted.current) setApiError("Failed to execute query");
      } finally {
        if (isMounted.current) setIsLoading(false);
      }
    };

    fetchVehicleData();
    return () => { fetchTriggered.current = false; };
  }, [vehicleNumber, startDate, endDate]);

  if (isLoading) return <div style={{ textAlign: "center" }}>Loading...</div>;
  if (apiError) return <div>{apiError}</div>;
  if (!data.length) return <div style={{ textAlign: "center" }}>No data available</div>;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 10, textAlign: "center" }}>
        <span style={{ color: "#3CB371", marginRight: 24, fontWeight: 600 }}>&#9632; Safe Speed (≤ 40 km/h)</span>
        <span style={{ color: "#FFA500", marginRight: 24, fontWeight: 600 }}>&#9632; Slightly Above Threshold (41-60 km/h)</span>
        <br />
        <span style={{ color: "#FF0000", fontWeight: 600 }}>&#9632; Over-Speeding (&gt; 60 km/h)</span>
      </div>
      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="vehicle_tracking_time" tickFormatter={formatTimeDisplay}
            label={{ value: "Time (HH:MM)", position: "insideBottomRight", offset: 0 }} minTickGap={16} height={35} />
          <YAxis label={{ value: "Speed (km/h)", angle: -90, position: "insideLeft" }} />
          <Tooltip formatter={formatTooltip} labelFormatter={(label) => `Time: ${label}`} />
          <ReferenceLine y={40} stroke="#3CB371" strokeDasharray="3 3" label="Safe Limit" />
          <ReferenceLine y={60} stroke="#FF0000" strokeDasharray="3 3" label="Over-speed Limit" />
          <Line type="monotone" dataKey="speed" stroke="#8884d8" strokeWidth={2}
            dot={<CustomDot />} activeDot={{ r: 6 }} name="Speed" />
          <Brush dataKey="vehicle_tracking_time" height={26} stroke="#8884d8"
            tickFormatter={formatTimeDisplay} y={370} travellerWidth={7} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const GpsTravelView = () => {
  const location = useLocation();
  const { data, vehicleType: _vehicleType, vehicleData: vehicleInfo } = location.state || {};
  const vehicleType = _vehicleType || data?.vehicleType || data?.vehicle_type || '';

  // ── Shared loader — same id + libraries as all other map components ────────
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // ── State ──────────────────────────────────────────────────────────────────
  const [vehicleTrackingData, setVehicleTrackingData] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const [apiError, setApiError] = useState("");
  const [showDataPoints, setShowDataPoints] = useState(false);
  const [roadSnappingEnabled, setRoadSnappingEnabled] = useState(false);
  const [showSpeedHistory, setShowSpeedHistory] = useState(false);
  const [speedHistoryData, setSpeedHistoryData] = useState(null);
  const [isTripSummaryLoading, setIsTripSummaryLoading] = useState(false);
  const [mapType, setMapType] = useState("roadmap");
  const [showTransit, setShowTransit] = useState(false);
  const [routeType, setRouteType] = useState("actual");
  const [expectedRouteCoordinates, setExpectedRouteCoordinates] = useState([]);
  const [routeHaltPoints, setRouteHaltPoints] = useState([]);
  const [idleCalculationPoints, setIdleCalculationPoints] = useState([]);
  const [idlePeriods, setIdlePeriods] = useState([]);
  const [totalIdleTime, setTotalIdleTime] = useState({ totalMinutes: 0, formattedTotal: "0 minutes", idleCount: 0 });
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  const [isLoadingPolylines, setIsLoadingPolylines] = useState(false);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [datapointAddr, setDatapointAddr] = useState({ index: null, address: null, loading: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── Playback animation state ───────────────────────────────────────────────
  const [animPlaying,      setAnimPlaying]      = useState(false);
  const [animPos,          setAnimPos]          = useState(null);
  const [animHeading,      setAnimHeading]      = useState(0);
  const [showVehiclePopup, setShowVehiclePopup] = useState(false);
  const [animSpeed,        setAnimSpeed]        = useState(1);

  const animRef      = useRef(null);
  const animIdxRef   = useRef(0);
  const animStartRef = useRef(null);

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchTimerRef = useRef(null);
  const mapContainerRef = useRef(null);

  // ── Format Helpers ─────────────────────────────────────────────────────────
  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    const pad = (n) => String(n).padStart(2, "0");
    const ms = String(date.getMilliseconds()).padStart(3, "0");
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${ms}`
    );
  };

  const formatTrackingTime = (timeString) => {
    try {
      if (!timeString) return "N/A";
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      let hours = date.getHours();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")} ${ampm}`;
    } catch { return timeString; }
  };

  const formatDateTimeForDisplay = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      if (dateTimeString.includes("-") && dateTimeString.includes(":")) {
        const [datePart, timePart] = dateTimeString.split(" ");
        if (datePart && timePart) {
          const [hours, minutes] = timePart.split(":");
          return `${datePart} ${hours}:${minutes}`;
        }
      }
      const date = new Date(dateTimeString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}-${month}-${year} ${hours}:${minutes}`;
      }
      return dateTimeString;
    } catch { return dateTimeString; }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatEventType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // ── Idle Statistics Render ─────────────────────────────────────────────────
  const renderEnhancedIdleStatistics = () => (
    <div className="col-md-12 mt-3">
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ padding: "10px 18px", background: "#f8fafc", borderLeft: "4px solid #f59e0b", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#d97706", textTransform: "uppercase", letterSpacing: "0.4px" }}>Idle Time Analysis</span>
        </div>
        <div style={{ padding: "14px 18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderLeft: "3px solid #f97316", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Total Idle Time</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#f97316" }}>{totalIdleTime.formattedTotal}</div>
            </div>
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "3px solid #ef4444", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Idle Locations</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#ef4444" }}>{totalIdleTime.idleCount}</div>
            </div>
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderLeft: "3px solid #0f4a45", borderRadius: 8, padding: "10px 14px" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Avg Idle Duration</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f4a45" }}>
                {totalIdleTime.idleCount > 0 ? formatDuration(totalIdleTime.totalMinutes / totalIdleTime.idleCount) : "0 minutes"}
              </div>
            </div>
          </div>
          {idlePeriods.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Halt Locations</div>
              <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {idlePeriods.map((period, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", borderRadius: 8, padding: "10px 14px", border: "1px solid #e2e8f0", borderLeft: `3px solid ${index === 0 ? "#0f4a45" : "#94a3b8"}` }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: index === 0 ? "#0f4a45" : "#94a3b8", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                      {index + 1}
                    </div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", flex: 1 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Duration</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#f97316" }}>{formatDuration(period.duration)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Halt Start</div>
                        <div style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>{formatTrackingTime(period.startTime)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>Halt End</div>
                        <div style={{ fontSize: 12, color: "#334155", fontWeight: 500 }}>{formatTrackingTime(period.endTime)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Idle Time Calculation ──────────────────────────────────────────────────
  const calculateIdleTimeWithClustering = useCallback((gpsData, options = {}) => {
    const config = {
      maxIdleRadius: options.maxIdleRadius || 50,
      minIdleDuration: options.minIdleDuration || 5,
      speedThreshold: options.speedThreshold || 2,
      minPointsInCluster: options.minPointsInCluster || 5,
      maxTimeBetweenPoints: options.maxTimeBetweenPoints || 10,
      ...options,
    };
    if (gpsData.length < config.minPointsInCluster) return [];
    const validPoints = gpsData.map((point, index) => ({
      ...point, originalIndex: index, speed: parseFloat(point.speed || 0),
      lat: parseFloat(point.latitude), lng: parseFloat(point.longitude),
      timestamp: new Date(point.vehicle_tracking_time),
    })).filter((p) => !isNaN(p.lat) && !isNaN(p.lng) && !isNaN(p.speed) && !isNaN(p.timestamp.getTime()) && p.speed <= config.speedThreshold)
      .sort((a, b) => a.timestamp - b.timestamp);
    if (validPoints.length < config.minPointsInCluster) return [];

    const stationarySessions = [];
    let currentSession = [validPoints[0]];
    for (let i = 1; i < validPoints.length; i++) {
      const current = validPoints[i], previous = validPoints[i - 1];
      const timeGapMinutes = (current.timestamp - previous.timestamp) / (1000 * 60);
      const distance = calculateDistance(previous.lat, previous.lng, current.lat, current.lng) * 1000;
      if (timeGapMinutes <= config.maxTimeBetweenPoints && distance <= config.maxIdleRadius) {
        currentSession.push(current);
      } else {
        if (currentSession.length >= config.minPointsInCluster) stationarySessions.push(currentSession);
        currentSession = [current];
      }
    }
    if (currentSession.length >= config.minPointsInCluster) stationarySessions.push(currentSession);

    return stationarySessions.map((session, sessionIndex) => {
      const centerLat = session.reduce((s, p) => s + p.lat, 0) / session.length;
      const centerLng = session.reduce((s, p) => s + p.lng, 0) / session.length;
      const startTime = session[0].vehicle_tracking_time;
      const endTime = session[session.length - 1].vehicle_tracking_time;
      const duration = (new Date(endTime) - new Date(startTime)) / (1000 * 60);
      const avgSpeed = session.reduce((s, p) => s + p.speed, 0) / session.length;
      const maxRadius = Math.max(...session.map((p) => calculateDistance(centerLat, centerLng, p.lat, p.lng) * 1000));
      return {
        id: `halt-${sessionIndex}`, startTime, endTime, duration: Math.round(duration),
        location: { lat: centerLat, lng: centerLng }, pointsCount: session.length,
        startIndex: session[0].originalIndex, endIndex: session[session.length - 1].originalIndex,
        avgSpeed: avgSpeed.toFixed(1), clusterRadius: Math.round(maxRadius),
        significance: session.length / (duration || 1), timeSpan: duration,
        validHalt: duration >= config.minIdleDuration && avgSpeed <= config.speedThreshold,
      };
    }).filter((p) => p.validHalt).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, []);

  // ── Idle useEffect ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (vehicleTrackingData.length > 0 && idleCalculationPoints.length > 0) {
      const normalized = idleCalculationPoints.map((p, i) => ({
        id: p.id || `halt-${i}`,
        startTime: p.startTime || p.vehicle_tracking_time || "",
        endTime: p.endTime || p.vehicle_tracking_time || "",
        duration: typeof p.duration === "number" ? Math.round(p.duration) : 0,
        location: { lat: parseFloat(p.location?.lat ?? p.latitude ?? 0), lng: parseFloat(p.location?.lng ?? p.longitude ?? 0) },
        pointsCount: p.count || p.pointsCount || 0,
        count: p.count || 0, significance: p.significance || 1, validHalt: true, address: "",
      }));

      setIdlePeriods(normalized);
      const totalMinutes = normalized.reduce((t, p) => t + p.duration, 0);
      setTotalIdleTime({ totalMinutes, formattedTotal: formatDuration(totalMinutes), idleCount: normalized.length });

      const fetchIdleAddresses = async () => {
        const updated = await Promise.all(normalized.map(async (period) => {
          try {
            const addr = await getAddressFromLatLng(period.location.lat, period.location.lng);
            return { ...period, address: addr };
          } catch {
            return { ...period, address: `${period.location.lat.toFixed(6)}, ${period.location.lng.toFixed(6)}` };
          }
        }));
        setIdlePeriods(updated);
      };
      fetchIdleAddresses();
    }
  }, [vehicleTrackingData, idleCalculationPoints]);

  // ── Playback animation ─────────────────────────────────────────────────────
  useEffect(() => {
    const coords = vehicleTrackingData
      .map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

    if (!animPlaying || coords.length < 2) {
      cancelAnimationFrame(animRef.current);
      return;
    }

    // Resume from paused index (don't reset to 0 here — handlePlayPause manages that)
    const resumeIdx = Math.min(animIdxRef.current, coords.length - 2);
    animIdxRef.current   = resumeIdx;
    animStartRef.current = null;
    setAnimPos(coords[resumeIdx]);

    const SEG_MS = 300 / animSpeed;

    const tick = (ts) => {
      const idx = animIdxRef.current;
      if (idx + 1 >= coords.length) {
        setAnimPlaying(false);
        return;
      }
      if (animStartRef.current === null) animStartRef.current = ts;
      const t    = Math.min((ts - animStartRef.current) / SEG_MS, 1);
      const from = coords[idx];
      const to   = coords[idx + 1];
      setAnimPos({ lat: from.lat + (to.lat - from.lat) * t, lng: from.lng + (to.lng - from.lng) * t });
      if (t >= 1) {
        setAnimHeading(_calcBearing(from, to));
        animIdxRef.current   += 1;
        animStartRef.current  = null;
      }
      if (animIdxRef.current + 1 < coords.length) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setAnimPlaying(false);
      }
    };
    animRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animRef.current);
  }, [animPlaying, animSpeed, vehicleTrackingData]);

  const handlePlayPause = () => {
    const coords = vehicleTrackingData
      .map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng));

    if (animPlaying) {
      // Pause — preserve current index so resume continues from here
      cancelAnimationFrame(animRef.current);
      setAnimPlaying(false);
    } else {
      // If reached the end or never started, reset to beginning
      if (animIdxRef.current >= coords.length - 1) {
        animIdxRef.current = 0;
        setAnimHeading(0);
        setAnimPos(null);
      }
      animStartRef.current = null;
      setAnimPlaying(true);
    }
  };

  // ── GPS Helpers ────────────────────────────────────────────────────────────
  const validateAndFilterGPSPoints = (points, options = {}) => {
    const maxSpeed = options.maxSpeed || 150;
    if (!points || points.length <= 2) return points || [];
    const basicValid = points.filter((p) => {
      const lat = parseFloat(p.latitude), lng = parseFloat(p.longitude);
      return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && lat !== 0 && lng !== 0;
    });
    if (basicValid.length <= 2) return basicValid;
    const validated = [basicValid[0]];
    for (let i = 1; i < basicValid.length; i++) {
      const current = basicValid[i];
      const previous = validated[validated.length - 1];
      const distance = calculateDistance(parseFloat(previous.latitude), parseFloat(previous.longitude), parseFloat(current.latitude), parseFloat(current.longitude));
      const timeDiff = Math.abs(new Date(current.vehicle_tracking_time) - new Date(previous.vehicle_tracking_time)) / (1000 * 60 * 60);
      if (timeDiff < 0.001) continue;
      // Only reject physically impossible speed — no hard distance cap so long routes are preserved
      if (distance / timeDiff <= maxSpeed) validated.push(current);
    }
    return validated;
  };

  // Roads API snapToRoads — snaps each GPS point to the road the vehicle was
  // actually on, respecting travel direction. Falls back to DirectionsService
  // segments if the Roads API is not enabled on the key.
  const snapPointsToRoad = async (points) => {
    const valid = points.filter(p => {
      const la = parseFloat(p.lat), lo = parseFloat(p.lng);
      return !isNaN(la) && !isNaN(lo) && la >= -90 && la <= 90 && lo >= -180 && lo <= 180;
    });
    if (valid.length < 2) return points.map(p => [p.lat || 0, p.lng || 0]);

    // ── Pre-processing: speed-aware noise reduction ───────────────────────────
    const toRad = d => d * Math.PI / 180;
    const haversineM = (a, b) => {
      const dLat = toRad(parseFloat(b.lat) - parseFloat(a.lat));
      const dLng = toRad(parseFloat(b.lng) - parseFloat(a.lng));
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(parseFloat(a.lat))) * Math.cos(toRad(parseFloat(b.lat))) * Math.sin(dLng / 2) ** 2;
      return 2 * 6371000 * Math.asin(Math.sqrt(Math.min(1, h)));
    };

    // 1. Speed-aware deduplication:
    //    Moving (speed > 2 km/h) → always keep, no matter how close — Roads API needs
    //    every direction hint to pick the right road in urban areas.
    //    Stationary (speed ≈ 0) → keep only if > 5 m from last kept point to avoid
    //    flooding the batch with GPS chatter from long traffic stops.
    const deduped = [valid[0]];
    for (let i = 1; i < valid.length; i++) {
      const curr        = valid[i];
      const isStationary = (parseFloat(curr.speed) || 0) < 2;
      if (!isStationary || haversineM(deduped[deduped.length - 1], curr) >= 5) {
        deduped.push(curr);
      }
    }
    if (deduped[deduped.length - 1] !== valid[valid.length - 1]) deduped.push(valid[valid.length - 1]);

    // 2. Remove isolated GPS teleport jumps (> 500 m from BOTH neighbours)
    const cleaned = deduped.filter((p, i) => {
      if (i === 0 || i === deduped.length - 1) return true;
      const dPrev = haversineM(deduped[i - 1], p);
      const dNext = haversineM(p, deduped[i + 1]);
      if (dPrev > 500 && dNext > 500) {
        console.warn(`GPS outlier skipped: ${dPrev.toFixed(0)} m prev / ${dNext.toFixed(0)} m next`);
        return false;
      }
      return true;
    });

    let pts = cleaned.length >= 2 ? cleaned : valid;
    console.log(`GPS pre-process: ${valid.length} raw → ${deduped.length} deduped → ${pts.length} clean pts`);

    // ── Gap densification ─────────────────────────────────────────────────────
    // Roads API interpolation breaks down when consecutive GPS points are > 300 m
    // apart (sparse highway / tunnel / signal loss). For those specific gaps, use
    // Directions API — which follows road geometry between any two points — to
    // insert intermediate road points before snapping. Rest of the code unchanged.
    const GAP_M = 300;
    if (pts.some((p, i) => i > 0 && haversineM(pts[i - 1], p) > GAP_M) && window.google?.maps?.DirectionsService) {
      const densified = [pts[0]];
      for (let i = 1; i < pts.length; i++) {
        const gap = haversineM(pts[i - 1], pts[i]);
        if (gap > GAP_M) {
          if (densified.length > 1) await new Promise(r => setTimeout(r, 100));
          const a = pts[i - 1], b = pts[i];
          const midPts = await new Promise(resolve => {
            new window.google.maps.DirectionsService().route(
              {
                origin:      new window.google.maps.LatLng(parseFloat(a.lat), parseFloat(a.lng)),
                destination: new window.google.maps.LatLng(parseFloat(b.lat), parseFloat(b.lng)),
                travelMode:  window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
                  const path = [];
                  result.routes[0].legs.forEach(leg =>
                    leg.steps.forEach(step =>
                      step.path.forEach(pt =>
                        path.push({ lat: pt.lat(), lng: pt.lng(), speed: ((parseFloat(a.speed) || 0) + (parseFloat(b.speed) || 0)) / 2 })
                      )
                    )
                  );
                  resolve(path.slice(1, -1)); // exclude endpoints — already in densified
                } else {
                  resolve([]);
                }
              }
            );
          });
          console.log(`Gap fill: ${gap.toFixed(0)} m gap → ${midPts.length} road pts inserted`);
          densified.push(...midPts);
        }
        densified.push(pts[i]);
      }
      console.log(`Gap densification: ${pts.length} → ${densified.length} pts`);
      pts = densified.length >= 2 ? densified : pts;
    }

    // ── Turn detection ────────────────────────────────────────────────────────
    // Smoothed bearing uses a ±2 point window to avoid GPS noise at low speed.
    // A real turn = bearing shift > 45° where the vehicle is moving (> 5 km/h).
    const calcBearing = (a, b) => {
      const la1 = toRad(parseFloat(a.lat)), la2 = toRad(parseFloat(b.lat));
      const dLo  = toRad(parseFloat(b.lng) - parseFloat(a.lng));
      return (Math.atan2(Math.sin(dLo) * Math.cos(la2), Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLo)) * 180 / Math.PI + 360) % 360;
    };
    const angleDiff     = (b1, b2) => { const d = Math.abs(b1 - b2) % 360; return d > 180 ? 360 - d : d; };
    const smoothBearing = i => calcBearing(pts[Math.max(0, i - 2)], pts[Math.min(pts.length - 1, i + 2)]);

    const turnSet = new Set([0, pts.length - 1]);
    for (let i = 1; i < pts.length - 1; i++) {
      if ((parseFloat(pts[i].speed) || 0) < 5) continue;
      if (angleDiff(smoothBearing(i - 1), smoothBearing(i + 1)) > 45) turnSet.add(i);
    }
    console.log(`Turn detection: ${turnSet.size - 2} turns in ${pts.length} pts`);

    // ── Turn-aware batching ────────────────────────────────────────────────────
    // End each batch at a detected turn so the Roads API always starts a new
    // batch from a clean anchor on the correct road branch — preventing the API
    // from interpolating across a fork or taking the wrong parallel road.
    // Falls back to a fixed-size cut (with 5-pt overlap) when no turn is found.
    const MAX_B  = 93;
    const SZ_OVL = 5;
    const batches = [];
    let bStart = 0;

    while (bStart < pts.length - 1) {
      const limit = bStart + MAX_B;
      let bEnd    = pts.length - 1;
      let turnCut = true;

      if (limit < pts.length - 1) {
        let found = false;
        for (let j = Math.min(limit, pts.length - 2); j > bStart + Math.floor(MAX_B / 2); j--) {
          if (turnSet.has(j)) { bEnd = j; found = true; break; }
        }
        if (!found) { bEnd = limit; turnCut = false; }
      }

      batches.push({ slice: pts.slice(bStart, bEnd + 1), turnCut });
      bStart = turnCut ? bEnd : Math.max(bEnd - SZ_OVL + 1, bStart + 1);
    }
    console.log(`Batches: ${batches.length} (turn-anchored)`);

    // ── Roads API snapToRoads ─────────────────────────────────────────────────
    const snapped = [];
    let   roadsOk = false;

    for (let b = 0; b < batches.length; b++) {
      if (b > 0) await new Promise(r => setTimeout(r, 150));
      const { slice: batch, turnCut } = batches[b];
      const path = batch.map(p => `${parseFloat(p.lat).toFixed(7)},${parseFloat(p.lng).toFixed(7)}`).join('|');

      try {
        const res  = await fetch(
          `https://roads.googleapis.com/v1/snapToRoads?path=${path}&interpolate=true&key=${GOOGLE_MAPS_API_KEY}`
        );
        const data = await res.json();

        if (data.error) { console.warn('Roads API error:', data.error.message); break; }

        if (data.snappedPoints?.length > 0) {
          roadsOk = true;
          if (b === 0) {
            snapped.push(...data.snappedPoints.map(p => [p.location.latitude, p.location.longitude]));
          } else {
            // Turn cut → 1 shared anchor point → skip snapped output for originalIndex 0
            // Size cut → SZ_OVL shared points → skip snapped output for originalIndex < SZ_OVL
            const skipTo   = turnCut ? 1 : SZ_OVL;
            const firstNew = data.snappedPoints.findIndex(
              p => p.originalIndex !== undefined && p.originalIndex >= skipTo
            );
            const toAdd = firstNew >= 0 ? data.snappedPoints.slice(firstNew) : data.snappedPoints;
            snapped.push(...toAdd.map(p => [p.location.latitude, p.location.longitude]));
          }
        }
      } catch (err) {
        console.warn('Roads API batch failed:', err);
        break;
      }
    }

    if (roadsOk && snapped.length > pts.length) {
      console.log(`Roads API snapToRoads: ${pts.length} pts, ${batches.length} batches → ${snapped.length} snapped`);
      return snapped;
    }

    // ── Fallback: DirectionsService in short segments ─────────────────────────
    console.warn('Roads API unavailable — falling back to DirectionsService segments');

    const resample = (arr, n) => {
      if (arr.length <= n) return arr;
      const out = [], step = (arr.length - 1) / (n - 1);
      for (let j = 0; j < n; j++) out.push(arr[Math.min(Math.round(j * step), arr.length - 1)]);
      out[out.length - 1] = arr[arr.length - 1];
      return out;
    };

    const routeSegment = (segPts) => new Promise(resolve => {
      if (!window.google?.maps?.DirectionsService) {
        resolve({ path: segPts.map(p => [p.lat, p.lng]), ok: false }); return;
      }
      const sel  = resample(segPts, 25);
      const orig = new window.google.maps.LatLng(sel[0].lat, sel[0].lng);
      const dest = new window.google.maps.LatLng(sel[sel.length - 1].lat, sel[sel.length - 1].lng);
      const wpts = sel.slice(1, -1).map(p => ({ location: new window.google.maps.LatLng(p.lat, p.lng), stopover: false }));
      new window.google.maps.DirectionsService().route(
        { origin: orig, destination: dest, waypoints: wpts, travelMode: window.google.maps.TravelMode.DRIVING, optimizeWaypoints: false },
        (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
            const p = [];
            result.routes[0].legs.forEach(leg => leg.steps.forEach(step => step.path.forEach(pt => p.push([pt.lat(), pt.lng()]))));
            resolve({ path: p, ok: true });
          } else {
            resolve({ path: segPts.map(p => [p.lat, p.lng]), ok: false });
          }
        }
      );
    });

    const NUM_SEGS = pts.length > 150 ? 6 : pts.length > 75 ? 3 : 1;
    const SEG_SIZE = Math.ceil(pts.length / NUM_SEGS);
    const fullPath = [];
    let dsOk = false;

    for (let s = 0; s < NUM_SEGS; s++) {
      if (s > 0) await new Promise(r => setTimeout(r, 200));
      const start = s * SEG_SIZE;
      const end   = Math.min(pts.length, start + SEG_SIZE + 1);
      const { path, ok } = await routeSegment(pts.slice(start, end));
      if (ok) dsOk = true;
      fullPath.push(...(s === 0 ? path : path.slice(1)));
    }

    return dsOk && fullPath.length > pts.length ? fullPath : pts.map(p => [p.lat, p.lng]);
  };

  // ── Address Lookup (Google Maps Geocoding API) ────────────────────────────
  const getAddressFromLatLng = async (lat, lng, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await axios.get("https://maps.googleapis.com/maps/api/geocode/json", {
          params: { latlng: `${lat},${lng}`, key: GOOGLE_MAPS_API_KEY },
          timeout: 12000,
        });
        if (response.data?.status === "OK" && response.data.results?.length > 0) {
          return response.data.results[0].formatted_address;
        }
        throw new Error("No address data");
      } catch {
        if (attempt === maxRetries - 1) return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
      }
    }
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  useEffect(() => {
    if (vehicleTrackingData.length > 0) {
      const fetchAddresses = async () => {
        const start = vehicleTrackingData[0];
        const end = vehicleTrackingData[vehicleTrackingData.length - 1];
        const [startAddr, endAddr] = await Promise.all([
          getAddressFromLatLng(parseFloat(start.latitude), parseFloat(start.longitude)),
          getAddressFromLatLng(parseFloat(end.latitude), parseFloat(end.longitude)),
        ]);
        setStartAddress(startAddr);
        setEndAddress(endAddr);
      };
      fetchAddresses();
    }
  }, [vehicleTrackingData]);

  // ── Reverse-geocode address for clicked GPS data point ────────────────────
  useEffect(() => {
    if (activeInfoWindow?.type !== 'datapoint') return;
    const idx = activeInfoWindow.index;
    // Already cached for this index
    if (datapointAddr.index === idx && !datapointAddr.loading) return;
    const point = vehicleTrackingData[idx];
    if (!point) return;
    setDatapointAddr({ index: idx, address: null, loading: true });
    getAddressFromLatLng(parseFloat(point.latitude), parseFloat(point.longitude))
      .then(addr => setDatapointAddr({ index: idx, address: addr, loading: false }))
      .catch(() => setDatapointAddr({
        index: idx,
        address: `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`,
        loading: false,
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInfoWindow]);

  // ── Wait Time Status ───────────────────────────────────────────────────────
  const getWaitTimeStatus = (expectedMinutes, actualMinutes) => {
    if (!expectedMinutes || !actualMinutes) return null;
    const variance = Math.round(actualMinutes) - expectedMinutes;
    if (variance < 0) return { status: "fast", color: "#ffc107" };
    if (variance < 2) return { status: "good", color: "#28a745" };
    return { status: "slow", color: "#dc3545" };
  };

  // ── Main Data Fetch ────────────────────────────────────────────────────────
  const calculateStatistics = (data) => {
    if (data.length < 2) return;
    let totalDistance = 0, totalSpeed = 0, maxSpeed = 0, validSpeedPoints = 0;
    for (let i = 1; i < data.length; i++) {
      totalDistance += calculateDistance(parseFloat(data[i - 1].latitude), parseFloat(data[i - 1].longitude), parseFloat(data[i].latitude), parseFloat(data[i].longitude));
      if (data[i].speed) {
        const speed = parseFloat(data[i].speed);
        totalSpeed += speed; validSpeedPoints++;
        if (speed > maxSpeed) maxSpeed = speed;
      }
    }
  };

  const vehicleData = async (vehicleNumber, startDate, endDate) => {
    setIsLoadingData(true); setIsLoadingPolylines(true); setApiError("");
    try {
      const payload = { vehicleNumber, startTime: formatDateTime(startDate), endTime: formatDateTime(endDate) };
      const responseData = await postDataWithBody(fetchVehicleTrackingWithIdealPoints, payload, {});
      const validatedData = validateAndFilterGPSPoints(responseData.raw_data, { maxSpeed: 120, maxDistanceJump: 2, outlierThreshold: 2.5 });
      setVehicleTrackingData(validatedData);
      setIdleCalculationPoints(responseData.idle_events);
      calculateStatistics(validatedData);

      if (validatedData.length > 1) {
        setIsLoadingMap(true);
        const coordinates = validatedData.map((point, index) => ({ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude), originalIndex: index, timestamp: point.vehicle_tracking_time, speed: parseFloat(point.speed) || 0 }));
        if (coordinates.length >= 2) {
          toast.info("Routing along roads...", { autoClose: 3000 });
          const routedPath = await snapPointsToRoad(coordinates);
          // DirectionsService returns far more points than input (full turn-by-turn path)
          const isRoaded = routedPath.length > coordinates.length;
          setRouteCoordinates(routedPath);
          setRoadSnappingEnabled(isRoaded);
          if (isRoaded) {
            toast.success(`Road route loaded — ${validatedData.length} GPS points`, { autoClose: 2000 });
          } else {
            toast.warning("Using GPS track (Directions API unavailable)", { autoClose: 3000 });
          }
        }
        setIsLoadingMap(false);
      }
    } catch (err) {
      console.error("Failed to execute query:", err);
      toast.error("Failed to execute query");
      setVehicleTrackingData([]); setRouteCoordinates([]); setRoadSnappingEnabled(false);
    } finally {
      setIsLoadingData(false); setIsLoadingPolylines(false); setIsLoadingMap(false);
    }
  };

  useEffect(() => {
    if (data) vehicleData(data.vehicleNumber, data.actualStartDate, data.actualEndDate);
  }, [data]);

  // ── Route Halt Points ──────────────────────────────────────────────────────
  const fetchRouteHaltPoints = useCallback(async (routeId) => {
    try {
      const requestPayload = { id: data?.id, vehicleNumber: data?.vehicleNumber, routeId, startDate: formatDateTime(data?.actualStartDate), endDate: formatDateTime(data?.actualEndDate) };
      const response = await postDataWithBody(getWayPoints, requestPayload, {});
      if (response && response.length > 0) {
        const sortedPoints = response.sort((a, b) => a.sequenceNo - b.sequenceNo);
        const importantPoints = sortedPoints.filter((p) => ["start", "halt", "end", "stop"].includes(p.pointType?.toLowerCase()));
        const pointsWithAddresses = [];
        for (let i = 0; i < importantPoints.length; i++) {
          const point = importantPoints[i];
          if (i > 0) await new Promise((r) => setTimeout(r, 300));
          let address = null, attempts = 0;
          while (!address && attempts < 3) {
            try {
              address = await getAddressFromLatLng(parseFloat(point.latitude), parseFloat(point.longitude));
              if (!address?.includes(",")) throw new Error("Bad address");
            } catch { attempts++; if (attempts < 3) await new Promise((r) => setTimeout(r, 1000 * attempts)); }
          }
          pointsWithAddresses.push({ ...point, address: address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}` });
        }
        setRouteHaltPoints(pointsWithAddresses);
        const allCoords = sortedPoints.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
        // Use raw planned waypoints exactly — no road snapping / alternative routing
        setExpectedRouteCoordinates(allCoords.map((c) => [c.lat, c.lng]));
        toast.success("Assigned route loaded successfully!", { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Error fetching halt points:", error);
      toast.error("Failed to fetch route halt points");
      setRouteHaltPoints([]); setExpectedRouteCoordinates([]);
    }
  }, [data]);

  // ── Route Type Change ──────────────────────────────────────────────────────
  const handleRouteTypeChange = async (type) => {
    setRouteType(type);
    if (type === "actual") { setExpectedRouteCoordinates([]); setRouteHaltPoints([]); }
    else if (type === "both") {
      if (data?.routeId) { setIsLoadingPolylines(true); await fetchRouteHaltPoints(data.routeId); setIsLoadingPolylines(false); }
      else toast.warning("No route assigned to this vehicle", { position: "top-center", autoClose: 3000 });
    }
  };

  // ── Map Helpers ────────────────────────────────────────────────────────────
  // Memoized so GoogleMap's center prop gets the same object reference on re-renders
  // triggered by activeInfoWindow changes — prevents map panning back to start point on popup close
  const mapCenter = useMemo(() => {
    if (routeCoordinates.length > 0) return { lat: routeCoordinates[0][0], lng: routeCoordinates[0][1] };
    if (vehicleTrackingData.length > 0) return { lat: parseFloat(vehicleTrackingData[0].latitude), lng: parseFloat(vehicleTrackingData[0].longitude) };
    return DEFAULT_CENTER;
  }, [routeCoordinates, vehicleTrackingData]);

  const fitMapBounds = useCallback((map) => {
    if (!window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (routeType === "actual" || routeType === "both") {
      vehicleTrackingData.forEach((p) => bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));
      routeCoordinates.forEach((p) => bounds.extend({ lat: p[0], lng: p[1] }));
    }
    if (routeType === "both") expectedRouteCoordinates.forEach((p) => bounds.extend({ lat: p[0], lng: p[1] }));
    if (!bounds.isEmpty()) map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
  }, [vehicleTrackingData, routeCoordinates, expectedRouteCoordinates, routeType]);

  const searchLocation = useCallback((query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    if (!autocompleteRef.current) return;
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
      (predictions, status) => {
        setIsSearching(false);
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status === OK && predictions?.length) {
          setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
        } else { setSearchResults([]); }
      }
    );
  }, []);

  const selectSearchResult = useCallback((result) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        if (mapRef.current) {
          if (results[0].geometry.viewport) mapRef.current.fitBounds(results[0].geometry.viewport);
          else { mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() }); mapRef.current.setZoom(15); }
        }
        toast.success(`Map centred on: ${result.name.split(",")[0]}`);
      }
    });
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    fitMapBounds(map);
  }, [fitMapBounds]);
  useEffect(() => { if (mapRef.current) fitMapBounds(mapRef.current); }, [fitMapBounds]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch((err) => console.warn("Fullscreen error:", err));
    } else {
      document.exitFullscreen();
    }
  }, []);

  // ── Icon Helpers ───────────────────────────────────────────────────────────
  // ── Icon helpers — teardrop pins matching RouteList / AssignRoute ─────────
  const makePinSvg = (fillColor) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
        fill="${fillColor}" stroke="white" stroke-width="1.5"/>
      <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  const makeHaltSvgIcon = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
      <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
      <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
      <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // green = start (#34A853), red = end (#EA4335)
  // lightgreen / lightred = assigned route start/end (yellow / orange)
  const makePinIcon = (color) => {
    if (!isLoaded || !window.google) return undefined;
    const colorMap = {
      green:       "#34A853",
      red:         "#EA4335",
      lightgreen:  "#F9A825",
      "light-green": "#F9A825",
      lightred:    "#FB8C00",
      "light-red": "#FB8C00",
    };
    const fillColor = colorMap[color] || "#EA4335";
    return {
      url: makePinSvg(fillColor),
      scaledSize: new window.google.maps.Size(28, 44),
      anchor: new window.google.maps.Point(14, 44),
    };
  };

  // For assigned-route halt points — use halt SVG (pause bars) for halt,
  // teardrop pin for start/end/stop
  const makeHaltPointIcon = (pointType, index, waitTimeStatus) => {
    if (!isLoaded || !window.google) return undefined;
    const t = pointType?.toLowerCase();
    if (t === "halt") {
      return {
        url: makeHaltSvgIcon(),
        scaledSize: new window.google.maps.Size(34, 34),
        anchor: new window.google.maps.Point(17, 17),
      };
    }
    // start → green pin, end/stop → red pin
    const fillColor = t === "start" ? "#34A853" : "#EA4335";
    return {
      url: makePinSvg(fillColor),
      scaledSize: new window.google.maps.Size(28, 44),
      anchor: new window.google.maps.Point(14, 44),
    };
  };

  const makeIdleIcon = (idlePeriod, index) => {
    const dur = idlePeriod.duration || 0;
    let bgColor = dur <= 15 ? "#22c55e" : dur <= 30 ? "#f97316" : "#ef4444";
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="58" viewBox="0 0 48 58">
      <defs><filter id="glow"><feGaussianBlur stdDeviation="2" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
      <circle cx="24" cy="30" r="18" fill="${bgColor}" opacity="0.3"><animate attributeName="r" values="18;22;18" dur="2s" repeatCount="indefinite"/></circle>
      <path d="M24 2 L24 2 C14 2 6 10 6 20 C6 34 24 54 24 54 C24 54 42 34 42 20 C42 10 34 2 24 2 Z" fill="${bgColor}" stroke="white" stroke-width="3" filter="url(#glow)"/>
      <circle cx="24" cy="20" r="12" fill="white" stroke="${bgColor}" stroke-width="2"/>
      <text x="24" y="24" font-family="Arial" font-size="12" font-weight="bold" fill="${bgColor}" text-anchor="middle" dominant-baseline="middle">${index + 1}</text>
      <circle cx="36" cy="10" r="8" fill="white" stroke="${bgColor}" stroke-width="2"/>
      <text x="36" y="13" font-family="Arial" font-size="10" font-weight="bold" fill="${bgColor}" text-anchor="middle">${idlePeriod.count || ""}</text>
    </svg>`;
    return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: isLoaded ? new window.google.maps.Size(48, 58) : undefined, anchor: isLoaded ? new window.google.maps.Point(24, 54) : undefined };
  };

  const makeDotIcon = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
      <circle cx="10" cy="10" r="6" fill="#1a7a73" stroke="white" stroke-width="2"><animate attributeName="r" values="6;7;6" dur="1.5s" repeatCount="indefinite"/></circle>
    </svg>`;
    return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: isLoaded ? new window.google.maps.Size(20, 20) : undefined, anchor: isLoaded ? new window.google.maps.Point(10, 10) : undefined };
  };

  // ── InfoWindow content helpers ─────────────────────────────────────────────
  const renderIdleInfoContent = (idlePeriod, index) => {
    const dur = idlePeriod.duration || 0;
    let badgeColor = "#22c55e", badgeLabel = "Short Stop", badgeIcon = "🅿️";
    if (dur > 15 && dur <= 30) { badgeColor = "#f97316"; badgeLabel = "Medium Idle"; badgeIcon = "⏸️"; }
    if (dur > 30) { badgeColor = "#ef4444"; badgeLabel = "⚠️ Long Idle"; badgeIcon = "⚠️"; }
    return (
      <div style={{ minWidth: "300px", fontFamily: "'Segoe UI', Roboto, sans-serif", borderRadius: "12px", overflow: "hidden", position: "relative" }}>
        <button onClick={() => setActiveInfoWindow(null)} style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.15)", border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer", fontSize: "14px", color: "#333", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, zIndex: 10 }}>✕</button>
        <div style={{ background: `linear-gradient(135deg, ${badgeColor}20, ${badgeColor}40)`, padding: "16px", borderBottom: `3px solid ${badgeColor}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "32px", background: "white", width: "48px", height: "48px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}>{badgeIcon}</div>
            <div><div style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>Idle Stop</div><div style={{ fontSize: "20px", fontWeight: "700", color: "#1f2937" }}>#{index + 1}</div></div>
            <div style={{ marginLeft: "auto", paddingRight: "28px" }}>
              <div style={{ background: badgeColor, color: "white", padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" }}>{badgeLabel}</div>
            </div>
          </div>
        </div>
        <div style={{ padding: "16px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>⏱️ Duration</div>
            <div style={{ fontSize: "28px", fontWeight: "700", color: badgeColor }}>{formatDuration(dur)}</div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", padding: "16px", background: "white" }}>
          <div style={{ background: "#f0f9ff", borderRadius: "8px", padding: "12px" }}><div style={{ fontSize: "11px", color: "#1a7a73", fontWeight: "600", marginBottom: "4px" }}>▶ START</div><div style={{ fontSize: "12px", color: "#1f2937", fontWeight: "500" }}>{formatTrackingTime(idlePeriod.startTime)}</div></div>
          <div style={{ background: "#fef2f2", borderRadius: "8px", padding: "12px" }}><div style={{ fontSize: "11px", color: "#dc2626", fontWeight: "600", marginBottom: "4px" }}>■ END</div><div style={{ fontSize: "12px", color: "#1f2937", fontWeight: "500" }}>{formatTrackingTime(idlePeriod.endTime)}</div></div>
        </div>
        <div style={{ padding: "16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
          <div style={{ background: "white", borderRadius: "8px", padding: "12px" }}>
            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>📍 LOCATION</div>
            <div style={{ fontSize: "12px", color: "#334155", lineHeight: "1.5" }}>{idlePeriod.address || `${idlePeriod.location.lat.toFixed(6)}, ${idlePeriod.location.lng.toFixed(6)}`}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderHaltInfoContent = (haltPoint) => {
    const actualWaitTime = haltPoint.haltDuration, expectedWaitTime = haltPoint.waitTime;
    const waitTimeStatus = getWaitTimeStatus(expectedWaitTime, actualWaitTime);
    const ptType = haltPoint.pointType?.toLowerCase();
    const colorMap = {
      start: { c: "#22c55e", d: "#16a34a", tint: "#f0fdf4" },
      end:   { c: "#ef4444", d: "#dc2626", tint: "#fef2f2" },
      halt:  { c: "#f59e0b", d: "#d97706", tint: "#fffbeb" },
      way:   { c: "#1a7a73", d: "#1a7a73", tint: "#f0fdfb" },
      stop:  { c: "#f59e0b", d: "#d97706", tint: "#fffbeb" },
    };
    const { c, d, tint } = colorMap[ptType] || { c: "#64748b", d: "#475569", tint: "#f8fafc" };
    const isHaltType = ptType === "halt" || ptType === "stop";
    const subtitleMap = { start: "Route starting location", end: "Route ending location", halt: "Scheduled stop location", way: "Waypoint location", stop: "Stop location" };
    const typeLabel = haltPoint.pointType.charAt(0).toUpperCase() + haltPoint.pointType.slice(1);
    return (
      <div style={{ minWidth: 280, maxWidth: 320, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
        <div style={{ background: `linear-gradient(135deg, ${c} 0%, ${d} 100%)`, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {isHaltType
              ? <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{typeLabel} Point</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{subtitleMap[ptType] || "Route location"}</div>
          </div>
          <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: routeType === "both" && expectedWaitTime > 0 ? 10 : 0 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: tint, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
              <div style={{ fontSize: 11, color: "#475569", background: tint, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${c}`, lineHeight: 1.5 }}>
                {haltPoint.address || `${parseFloat(haltPoint.latitude).toFixed(6)}, ${parseFloat(haltPoint.longitude).toFixed(6)}`}
              </div>
            </div>
          </div>
          {routeType === "both" && expectedWaitTime > 0 && (
            <div style={{ background: waitTimeStatus ? `${waitTimeStatus.color}12` : "#f8fafc", borderRadius: 8, padding: "10px 12px", border: `1px solid ${waitTimeStatus?.color || "#e2e8f0"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#1f2937" }}>Wait Time Analysis</div>
                {waitTimeStatus && (
                  <span style={{ background: waitTimeStatus.color, color: "white", padding: "3px 8px", borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                    {waitTimeStatus.status === "fast" ? "Fast" : waitTimeStatus.status === "good" ? "On Time" : "Slow"}
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ background: "#fff", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600, marginBottom: 3 }}>Expected</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1a7a73" }}>{formatDuration(expectedWaitTime)}</div>
                </div>
                <div style={{ background: "#fff", borderRadius: 6, padding: "8px 10px" }}>
                  <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600, marginBottom: 3 }}>Actual</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: actualWaitTime ? waitTimeStatus?.color || "#22c55e" : "#94a3b8" }}>
                    {actualWaitTime ? formatDuration(actualWaitTime) : "No stop"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStartEndInfoContent = (type, pointData) => {
    const isStart = type.toLowerCase().includes("start");
    const color   = isStart ? "#22c55e" : "#ef4444";
    const darkClr = isStart ? "#16a34a" : "#dc2626";
    const tintBg  = isStart ? "#f0fdf4"  : "#fef2f2";
    const subtitle = isStart ? "Route starting location" : "Route ending location";
    const timeLabel = isStart ? "Start Time" : "End Time";
    return (
      <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
        <div style={{ background: `linear-gradient(135deg, ${color} 0%, ${darkClr} 100%)`, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{type}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{subtitle}</div>
          </div>
          <button onClick={() => setActiveInfoWindow(null)} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
          {pointData.time && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{timeLabel}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{pointData.time}</div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
              <div style={{ fontSize: 11, color: "#475569", background: tintBg, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${color}`, lineHeight: 1.5 }}>
                {pointData.address || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Loading address…</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Computed polyline paths ────────────────────────────────────────────────
  const actualPolylinePath = routeCoordinates.map((c) => ({ lat: c[0], lng: c[1] }));
  const expectedPolylinePath = expectedRouteCoordinates.map((c) => ({ lat: c[0], lng: c[1] }));

  if (loadError) return <div className="alert alert-danger mt-3">Failed to load Google Maps. Please check your API key.</div>;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="row" style={{ margin: 0, alignItems: "stretch" }}>
        {/* ── Left Panel ── */}
        <div className="col-lg-4" style={{ display: "flex", flexDirection: "column", padding: "14px 8px 14px 16px" }}>
          <div style={{ flex: 1, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", padding: "16px", display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Section label */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 3, height: 14, borderRadius: 2, background: "#0f4a45" }}></div>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.9px" }}>Vehicle &amp; Route Information</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Vehicle Number */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#f0f7ff", border:"1px solid #99f6e4", borderRadius:10, borderLeft:"4px solid #0f4a45", boxShadow:"0 1px 4px rgba(26,122,115,0.10)" }}>
            <div style={{ width:42, height:42, borderRadius:10, background:"#ccfbf1", color:"#0f4a45", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>
            </div>
            <div>
              <div style={{ fontSize:10, color:"#0f4a45", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:4 }}>Vehicle Number</div>
              <div style={{ fontSize:15, fontWeight:700, color:"#1e293b" }}>{data?.vehicleNumber || "N/A"}</div>
            </div>
          </div>
          {/* Route Name */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, borderLeft:"4px solid #10b981", boxShadow:"0 1px 4px rgba(16,185,129,0.10)" }}>
            <div style={{ width:42, height:42, borderRadius:10, background:"#d1fae5", color:"#10b981", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l7-7 7 7M10 21v-8h4v8"/><path d="M3 17h18"/></svg>
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:10, color:"#10b981", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:4 }}>Route Name</div>
              <div style={{ fontSize:14, fontWeight:700, color:"#1e293b", wordBreak:"break-word", lineHeight:1.3 }}>{data?.routeName || "N/A"}</div>
            </div>
          </div>
          {/* Travelled Date & Time */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#f0fdfb", border:"1px solid #99f6e4", borderRadius:10, borderLeft:"4px solid #1a7a73", boxShadow:"0 1px 4px rgba(59,130,246,0.10)" }}>
            <div style={{ width:42, height:42, borderRadius:10, background:"#ccfbf1", color:"#1a7a73", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 14 11 16 15 12"/></svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#1a7a73", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:6 }}>Travelled Date &amp; Time</div>
              <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>Start</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#1e293b" }}>{formatTrackingTime(data?.actualStartDate) || "N/A"}</div>
                </div>
                <div style={{ width:1, backgroundColor:"#99f6e4", height:28, alignSelf:"center" }}></div>
                <div>
                  <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>End</div>
                  <div style={{ fontSize:12, fontWeight:600, color:"#1e293b" }}>{formatTrackingTime(data?.actualEndDate) || "N/A"}</div>
                </div>
              </div>
            </div>
          </div>
          {/* Planned Date & Time (only in "both" mode) */}
          {routeType !== "actual" && (
            <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:10, borderLeft:"4px solid #8b5cf6", boxShadow:"0 1px 4px rgba(139,92,246,0.10)" }}>
              <div style={{ width:42, height:42, borderRadius:10, background:"#ede9fe", color:"#8b5cf6", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:10, color:"#8b5cf6", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:6 }}>Planned Date &amp; Time</div>
                <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>Start</div>
                    <div style={{ fontSize:12, fontWeight:600, color:"#1e293b" }}>{data ? formatTrackingTime(data.startDate) : "-"}</div>
                  </div>
                  <div style={{ width:1, backgroundColor:"#e9d5ff", height:28, alignSelf:"center" }}></div>
                  <div>
                    <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:3 }}>End</div>
                    <div style={{ fontSize:12, fontWeight:600, color:"#1e293b" }}>{data ? formatTrackingTime(data.endDate) : "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
            </div>{/* end info cards */}
            <div style={{ borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />
            <div>
              <label style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6, display: "block" }}>Route Type</label>
              <select className="form-select form-select-sm" value={routeType} onChange={(e) => handleRouteTypeChange(e.target.value)} style={{ width: "100%", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 7, background: "#fff", color: "#1e293b", padding: "5px 10px" }}>
                <option value="actual">Travelled Route</option>
                <option value="both">Travelled &amp; Planned Routes</option>
              </select>
            </div>
            <button
              type="button"
              style={{ width: "100%", background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)", color: "#fff", border: "none", borderRadius: 7, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px rgba(26,122,115,0.25)" }}
              onClick={() => { setIsTripSummaryLoading(true); setShowSpeedHistory(true); }}
            >
              View Speed History &amp; Trip Summary
            </button>
            {routeType === "both" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 20, height: 3, backgroundColor: "#22c55e", border: "1px dashed #22c55e" }}></div><span style={{ fontSize: 12, color: "#64748b" }}>Planned</span></div>
                <div style={{ width: 1, height: 14, background: "#e2e8f0" }}></div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 20, height: 3, backgroundColor: "#ef4444" }}></div><span style={{ fontSize: 12, color: "#64748b" }}>Travelled</span></div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: roadSnappingEnabled ? "#ff4444" : "#4444ff" }}></div>
                <span style={{ fontSize: 12, color: "#64748b" }}>Travelled Route</span>
              </div>
            )}
            {apiError && <div className="alert alert-danger p-2 mt-1" style={{ fontSize: 12 }}><strong>API Error: </strong>{apiError}</div>}
          </div>
        </div>{/* end Left Panel */}
        {/* ── Right Panel ── */}
        <div className="col-lg-8" style={{ display: "flex", flexDirection: "column", padding: "14px 16px 14px 8px" }}>
          {/* ── Map Card ── */}
          <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.09)", display: "flex", flexDirection: "column" }}>
        {/* Gradient header */}
        <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/>
            </svg>
          </div>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>Route Map</span>
          <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
            {(LAYERS.find(l => l.id === mapType)?.label ?? mapType)} layer
          </span>
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Full Map View" : "Full Map View"}
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 6, padding: "4px 10px", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {isFullscreen
                ? <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/>
                : <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
              }
            </svg>
            {isFullscreen ? "Exit Full View" : "Full Map View"}
          </button>
        </div>

        {/* Search bar */}
        <div style={{ padding: "10px 14px 0", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <input type="text" placeholder="Search city, area, landmark or coordinates…"
              value={searchQuery} onChange={handleSearchInputChange}
              style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
            {searchQuery && (
              <button type="button" onClick={clearSearch}
                style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
            )}
            {isSearching && (
              <div style={{ padding: "0 10px" }}>
                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
              </div>
            )}
          </div>
          {searchResults.length > 0 && (
            <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
              {searchResults.map((result) => (
                <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                  className="search-result-row"
                  style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map Container */}
        <div
          ref={mapContainerRef}
          style={{ background: "white", flex: isFullscreen ? "none" : 1, height: isFullscreen ? "100vh" : "auto", minHeight: isFullscreen ? undefined : "520px", position: "relative", overflow: "hidden" }}
        >
        {(isLoadingData || isLoadingPolylines || isLoadingMap || !isLoaded) && (
          <LoadingOverlay message={!isLoaded ? "Loading Google Maps..." : isLoadingData ? "Fetching GPS data..." : isLoadingPolylines ? "Processing route..." : isLoadingMap ? "Rendering map..." : "Loading..."} />
        )}

        {isLoaded && (
          <div style={{ position: "relative", height: "100%", width: "100%" }}>
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={mapCenter}
              zoom={vehicleTrackingData.length > 0 ? 12 : 7}
              mapTypeId={mapType}
              options={{ mapTypeControl: false, streetViewControl: true, fullscreenControl: true, zoomControl: true, gestureHandling: "greedy", clickableIcons: false }}
              onLoad={onMapLoad}
            >
              {!isLoadingPolylines && !isLoadingMap && (
                <>
                  {routeType === "both" && expectedPolylinePath.length > 1 && (
                    <Polyline path={expectedPolylinePath} options={{ strokeColor: "#22c55e", strokeWeight: 4, strokeOpacity: 0.8, icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, scale: 4 }, offset: "0", repeat: "20px" }] }} />
                  )}
                  {(routeType === "actual" || routeType === "both") && actualPolylinePath.length > 1 && (
                    <Polyline path={actualPolylinePath} options={{ strokeColor: routeType === "both" ? "#ef4444" : roadSnappingEnabled ? "#ff4444" : "#4444ff", strokeWeight: routeType === "both" ? 3 : 4, strokeOpacity: routeType === "both" ? 0.7 : 0.8 }} />
                  )}

                  {idlePeriods.map((idlePeriod, index) => (
                    <Marker key={`idle-${index}`} position={{ lat: idlePeriod.location.lat, lng: idlePeriod.location.lng }} icon={makeIdleIcon(idlePeriod, index)} zIndex={100} onClick={() => setActiveInfoWindow({ type: "idle", index })}>
                      {activeInfoWindow?.type === "idle" && activeInfoWindow.index === index && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderIdleInfoContent(idlePeriod, index)}</InfoWindow>)}
                    </Marker>
                  ))}

                  {routeType === "both" && routeHaltPoints.filter((hp) => hp.pointType.toLowerCase() !== "route_point").map((haltPoint, index) => {
                    const waitTimeStatus = getWaitTimeStatus(haltPoint.waitTime, haltPoint.haltDuration);
                    return (
                      <Marker key={`halt-${haltPoint.id}`} position={{ lat: parseFloat(haltPoint.latitude), lng: parseFloat(haltPoint.longitude) }} icon={makeHaltPointIcon(haltPoint.pointType, index, waitTimeStatus)} zIndex={100} onClick={() => setActiveInfoWindow({ type: "halt", id: haltPoint.id })}>
                        {activeInfoWindow?.type === "halt" && activeInfoWindow.id === haltPoint.id && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderHaltInfoContent(haltPoint)}</InfoWindow>)}
                      </Marker>
                    );
                  })}

                  {routeType === "both" ? (
                    <>
                      {expectedRouteCoordinates.length > 0 && routeHaltPoints.length > 0 && (
                        <>
                          <Marker position={{ lat: expectedRouteCoordinates[0][0], lng: expectedRouteCoordinates[0][1] }} icon={makePinIcon("lightgreen")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "expectedStart" })}>
                            {activeInfoWindow?.type === "expectedStart" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("Assigned Start", { time: formatTrackingTime(data?.startDate), address: routeHaltPoints.find((p) => p.pointType.toLowerCase() === "start")?.address || `${expectedRouteCoordinates[0][0].toFixed(6)}, ${expectedRouteCoordinates[0][1].toFixed(6)}` })}</InfoWindow>)}
                          </Marker>
                          {expectedRouteCoordinates.length > 1 && (
                            <Marker position={{ lat: expectedRouteCoordinates[expectedRouteCoordinates.length - 1][0], lng: expectedRouteCoordinates[expectedRouteCoordinates.length - 1][1] }} icon={makePinIcon("lightred")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "expectedEnd" })}>
                              {activeInfoWindow?.type === "expectedEnd" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("Assigned End", { time: formatTrackingTime(data?.endDate), address: (routeHaltPoints.find((p) => p.pointType.toLowerCase() === "end")?.address || routeHaltPoints.find((p) => p.pointType.toLowerCase() === "stop")?.address) || `${expectedRouteCoordinates[expectedRouteCoordinates.length - 1][0].toFixed(6)}, ${expectedRouteCoordinates[expectedRouteCoordinates.length - 1][1].toFixed(6)}` })}</InfoWindow>)}
                            </Marker>
                          )}
                        </>
                      )}
                      {routeCoordinates.length > 0 && (
                        <>
                          <Marker position={{ lat: routeCoordinates[0][0], lng: routeCoordinates[0][1] }} icon={makePinIcon("green")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "actualStart" })}>
                            {activeInfoWindow?.type === "actualStart" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("Travelled Start", { time: vehicleTrackingData[0] ? formatTrackingTime(vehicleTrackingData[0].vehicle_tracking_time) : null, address: startAddress || `${routeCoordinates[0][0].toFixed(6)}, ${routeCoordinates[0][1].toFixed(6)}` })}</InfoWindow>)}
                          </Marker>
                          {routeCoordinates.length > 1 && (
                            <Marker position={{ lat: routeCoordinates[routeCoordinates.length - 1][0], lng: routeCoordinates[routeCoordinates.length - 1][1] }} icon={makePinIcon("red")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "actualEnd" })}>
                              {activeInfoWindow?.type === "actualEnd" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("Travelled End", { time: vehicleTrackingData[vehicleTrackingData.length - 1] ? formatTrackingTime(vehicleTrackingData[vehicleTrackingData.length - 1].vehicle_tracking_time) : null, address: endAddress || `${routeCoordinates[routeCoordinates.length - 1][0].toFixed(6)}, ${routeCoordinates[routeCoordinates.length - 1][1].toFixed(6)}` })}</InfoWindow>)}
                            </Marker>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {routeCoordinates.length > 0 && (
                        <Marker position={{ lat: routeCoordinates[0][0], lng: routeCoordinates[0][1] }} icon={makePinIcon("green")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "start" })}>
                          {activeInfoWindow?.type === "start" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("Start Point", { time: vehicleTrackingData[0] ? formatTrackingTime(vehicleTrackingData[0].vehicle_tracking_time) : null, address: startAddress || `${routeCoordinates[0][0].toFixed(6)}, ${routeCoordinates[0][1].toFixed(6)}` })}</InfoWindow>)}
                        </Marker>
                      )}
                      {routeCoordinates.length > 1 && (
                        <Marker position={{ lat: routeCoordinates[routeCoordinates.length - 1][0], lng: routeCoordinates[routeCoordinates.length - 1][1] }} icon={makePinIcon("red")} zIndex={1000} onClick={() => setActiveInfoWindow({ type: "end" })}>
                          {activeInfoWindow?.type === "end" && (<InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>{renderStartEndInfoContent("End Point", { time: vehicleTrackingData[vehicleTrackingData.length - 1] ? formatTrackingTime(vehicleTrackingData[vehicleTrackingData.length - 1].vehicle_tracking_time) : null, address: endAddress || `${routeCoordinates[routeCoordinates.length - 1][0].toFixed(6)}, ${routeCoordinates[routeCoordinates.length - 1][1].toFixed(6)}` })}</InfoWindow>)}
                        </Marker>
                      )}
                    </>
                  )}

                  {/* Animated vehicle marker */}
                  {animPos && isLoaded && (
                    <Marker
                      position={animPos}
                      icon={getTravelVehicleIcon(vehicleType, animHeading)}
                      zIndex={2000}
                      onClick={() => setShowVehiclePopup(v => !v)}
                    >
                      {showVehiclePopup && (vehicleInfo || data) && (() => {
                        const fmtDt = (s) => {
                          if (!s) return null;
                          try {
                            const d = new Date(s);
                            if (isNaN(d)) return s;
                            const pad = n => String(n).padStart(2, '0');
                            const h = d.getHours(), ampm = h >= 12 ? 'PM' : 'AM';
                            return `${pad(d.getDate())}-${pad(d.getMonth()+1)}-${d.getFullYear()} ${pad(h%12||12)}:${pad(d.getMinutes())} ${ampm}`;
                          } catch { return s; }
                        };
                        const Row = ({ label, value, accent }) => (
                          <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 6, padding: "5px 0", borderBottom: "1px solid #f1f5f9" }}>
                            <span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>{label}</span>
                            <span style={{ color: accent || "#1e293b", fontSize: 12, fontWeight: accent ? 700 : 400, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
                          </div>
                        );
                        const SectionHead = ({ children }) => (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "linear-gradient(90deg,#0f4a45,#1a7a73)", padding: "3px 10px", borderRadius: 4, margin: "10px -12px 6px", textTransform: "uppercase", letterSpacing: "0.6px" }}>{children}</div>
                        );
                        const emoji = TRAVEL_VEHICLE_EMOJI[(vehicleType || '').toLowerCase().replace(/[\s_-]+/g, '')] || '🚘';
                        return (
                          <InfoWindow onCloseClick={() => setShowVehiclePopup(false)}>
                            <div style={{ width: 258, borderRadius: 12, overflow: "hidden", fontFamily: "'Segoe UI',sans-serif", fontSize: 12 }}>
                              {/* Header */}
                              <div style={{ background: "linear-gradient(135deg,#0f4a45,#1a7a73)", color: "#fff", padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "0.3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data?.vehicleNumber || vehicleInfo?.vehicleNumber || "Vehicle"}</div>
                                  {(vehicleType || vehicleInfo?.vehicleType) && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>{vehicleType || vehicleInfo?.vehicleType}</div>}
                                </div>
                                <button
                                  onClick={() => setShowVehiclePopup(false)}
                                  style={{ background: "rgba(255,255,255,0.22)", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", color: "#fff", fontSize: 14, fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                                >✕</button>
                              </div>

                              {/* Body */}
                              <div style={{ padding: "10px 12px 12px", background: "#fff" }}>
                                {/* Vehicle Details */}
                                {[
                                  ["Brand",     vehicleInfo?.brand    || null],
                                  ["Model",     vehicleInfo?.model    || null],
                                  ["Fuel Type", vehicleInfo?.fuelType || null],
                                  ["Reg. No.",  vehicleInfo?.registrationNumber || null],
                                ].some(([, v]) => v) && (
                                  <>
                                    <SectionHead>Vehicle</SectionHead>
                                    {[
                                      ["Brand",     vehicleInfo?.brand    || null],
                                      ["Model",     vehicleInfo?.model    || null],
                                      ["Fuel Type", vehicleInfo?.fuelType || null],
                                      ["Reg. No.",  vehicleInfo?.registrationNumber || null],
                                    ].filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={v} />)}
                                  </>
                                )}

                                {/* Route Details */}
                                <SectionHead>Route</SectionHead>
                                {[
                                  ["Route Name", data?.routeName || null],
                                  ["Status",     data?.status   || null],
                                  ["Start",      fmtDt(data?.actualStartDate || data?.startDate)],
                                  ["End",        fmtDt(data?.actualEndDate   || data?.endDate)],
                                ].filter(([, v]) => v).map(([l, v]) => (
                                  <Row key={l} label={l} value={v} accent={l === "Status" ? (v === "TRAVELED" ? "#16a34a" : "#f59e0b") : null} />
                                ))}

                                {/* Driver Details */}
                                {(vehicleInfo?.driverName || vehicleInfo?.driverPhone) && (
                                  <>
                                    <SectionHead>Driver</SectionHead>
                                    {[
                                      ["Name",    vehicleInfo?.driverName    || null],
                                      ["Phone",   vehicleInfo?.driverPhone   || null],
                                      ["License", vehicleInfo?.driverLicense || null],
                                      ["Expiry",  vehicleInfo?.licenseExpiry || null],
                                    ].filter(([, v]) => v).map(([l, v]) => <Row key={l} label={l} value={v} />)}
                                  </>
                                )}
                              </div>
                            </div>
                          </InfoWindow>
                        );
                      })()}
                    </Marker>
                  )}

                  {showDataPoints && (routeType === "actual" || routeType === "both") && vehicleTrackingData.map((point, index) => (
                    <Marker key={`datapoint-${index}`} position={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }} icon={makeDotIcon()} onClick={() => setActiveInfoWindow({ type: "datapoint", index })}>
                      {activeInfoWindow?.type === "datapoint" && activeInfoWindow.index === index && (
                        <InfoWindow onCloseClick={() => setActiveInfoWindow(null)}>
                          <div style={{ minWidth: "220px", fontFamily: "'Segoe UI', sans-serif", borderRadius: "10px", overflow: "hidden", position: "relative" }}>
                            <button onClick={() => setActiveInfoWindow(null)} style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.12)", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", fontSize: "13px", color: "#333", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1, zIndex: 10 }}>✕</button>
                            <div style={{ background: "linear-gradient(135deg, #ccfbf1, #99f6e4)", padding: "12px 36px 12px 12px", borderBottom: "2px solid #1a7a73" }}>
                              <h6 style={{ margin: 0, color: "#1a7a73", fontWeight: "700", fontSize: "13px" }}>📍 GPS Data Point</h6>
                            </div>
                            <div style={{ padding: "12px", background: "#fff", fontSize: "12px", lineHeight: "2" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                                <span style={{ color: "#64748b", fontWeight: "600", flexShrink: 0 }}>Time</span>
                                <span style={{ color: "#1f2937" }}>{formatTrackingTime(point.vehicle_tracking_time)}</span>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "flex-start" }}>
                                <span style={{ color: "#64748b", fontWeight: "600", flexShrink: 0 }}>Location</span>
                                <span style={{ color: "#1f2937", textAlign: "right", maxWidth: "200px", lineHeight: "1.4" }}>
                                  {datapointAddr.index === index
                                    ? (datapointAddr.loading
                                        ? <span style={{ color: "#94a3b8", fontStyle: "italic" }}>Fetching address…</span>
                                        : datapointAddr.address)
                                    : `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}`}
                                </span>
                              </div>
                              {point.speed && (
                                <div style={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                                  <span style={{ color: "#64748b", fontWeight: "600" }}>Speed</span>
                                  <span style={{ color: "#1f2937" }}>{point.speed} km/h</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </InfoWindow>
                      )}
                    </Marker>
                  ))}
                </>
              )}
              {showTransit && <TransitLayer />}
            </GoogleMap>

            {!isLoadingData && !isLoadingPolylines && <CustomLayerControl mapType={mapType} setMapType={setMapType} />}
            <button
              onClick={() => setShowTransit(p => !p)}
              title={showTransit ? "Hide transit" : "Show transit"}
              style={{ position: "absolute", top: 60, right: 10, zIndex: 1000, width: 40, height: 40, borderRadius: 8, background: showTransit ? "#1a7a73" : "white", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="2" width="16" height="16" rx="2"/>
                <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
              </svg>
            </button>
          </div>
        )}
        </div>{/* end Map Container */}
          </div>{/* end Map Card */}
          {/* GPS controls */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px 0", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              {(routeType === "actual" || routeType === "both") && vehicleTrackingData.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    onClick={handlePlayPause}
                    style={{
                      display: "flex", alignItems: "center", gap: 7,
                      padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                      background: animPlaying
                        ? "linear-gradient(135deg,#dc3545,#b02a37)"
                        : "linear-gradient(135deg,#28a745,#1e7e34)",
                      color: "#fff", fontWeight: 700, fontSize: 12,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.18)", transition: "background 0.2s",
                    }}
                  >
                    {animPlaying ? (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                        Pause
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21"/></svg>
                        Play Route
                      </>
                    )}
                  </button>
                  {/* Speed controls */}
                  <div style={{ display: "flex", alignItems: "center", gap: 3, background: "#f1f5f9", borderRadius: 8, padding: "3px 5px", border: "1px solid #e2e8f0" }}>
                    {[1, 2, 3, 4].map(s => (
                      <button
                        key={s}
                        onClick={() => setAnimSpeed(s)}
                        style={{
                          padding: "3px 8px", borderRadius: 6, border: "none", cursor: "pointer",
                          fontSize: 11, fontWeight: 700,
                          background: animSpeed === s ? "linear-gradient(135deg,#0f4a45,#1a7a73)" : "transparent",
                          color: animSpeed === s ? "#fff" : "#64748b",
                          transition: "all 0.15s",
                        }}
                      >{s}x</button>
                    ))}
                  </div>
                </div>
              )}
              {routeType === "both" && routeHaltPoints.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#64748b" }}>
                  <span style={{ fontWeight: 600 }}>Expected:</span>
                  {[{ color: "#51cf66", label: "Start" }, { color: "#ff6b6b", label: "Halt" }, { color: "#dc3545", label: "End" }].map(({ color, label }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: color }}></div><span>{label}</span></div>
                  ))}
                  <span style={{ color: "#cbd5e1", margin: "0 2px" }}>|</span>
                  <span style={{ fontWeight: 600 }}>Travelled:</span>
                  {[{ fill: "#28a745", label: "Start" }, { fill: "#dc3545", label: "End" }].map(({ fill, label }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={fill} /></svg>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              {routeType === "actual" ? `${vehicleTrackingData.length} GPS points` : `${vehicleTrackingData.length} GPS points vs ${expectedRouteCoordinates.length} expected waypoints`}
            </span>
          </div>
        </div>{/* end Right Panel */}
      </div>{/* end row */}

      {vehicleTrackingData.length > 0 && idlePeriods.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}><div className="row">{renderEnhancedIdleStatistics()}</div></div>
      )}

      {showSpeedHistory && <div className="modal-backdrop show"></div>}
      {showSpeedHistory && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-fullscreen-lg-down modal-dialog-centered" style={{ maxWidth: "98vw", width: "76vw", height: "95vh" }}>
            <div className="modal-content h-100" style={{ width: "100%" }}>
              <div className="modal-header" style={{ background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)", color: "#fff", borderBottom: "none", padding: "14px 20px" }}>
                <h4 className="modal-title fw-bold" style={{ color: "#fff", fontSize: 16 }}>View Speed History &amp; Trip Summary</h4>
                <button type="button" className="btn-close btn-close-white" onClick={() => { setShowSpeedHistory(false); setIsTripSummaryLoading(false); setSpeedHistoryData(null); }}></button>
              </div>
              <div className="modal-body d-flex flex-column h-100 p-4">
                <div className="row g-4 h-100">
                  <div className="col-xl-8">
                    <div className="card h-100 border-0 shadow-lg">
                      <div className="card-header" style={{ background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)", borderBottom: "none" }}><h6 className="mb-0 fw-semibold" style={{ color: "#fff" }}>Speed Analysis Chart</h6></div>
                      <div className="card-body p-4">
                        <SpeedHistoryComponent vehicleNumber={data.vehicleNumber} startDate={data.actualStartDate} endDate={data.actualEndDate}
                          onDataLoad={(summary) => { setSpeedHistoryData(summary); setIsTripSummaryLoading(false); }} />
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-4">
                    <div className="card h-100 border-0 shadow-lg">
                      <div className="card h-100 border-0 shadow">
                        <div className="card-header" style={{ background: "linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)", borderBottom: "none" }}><h6 className="mb-0 fw-semibold" style={{ color: "#fff" }}>Trip Summary</h6></div>
                        <div className="card-body">
                          {isTripSummaryLoading && !speedHistoryData ? (
                            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 200 }}>
                              <div className="spinner-border text-info mb-3" role="status"><span className="visually-hidden">Loading...</span></div>
                              <div style={{ fontSize: 13, color: "#666" }}>Fetching trip summary...</div>
                            </div>
                          ) : speedHistoryData ? (
                            <div className="trip-summary-content">
                              <div className="row g-3">
                                <div className="col-12"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block" style={{ textAlign: "center" }}>Vehicle Number</small><div className="fw-bold" style={{ textAlign: "center" }}>{speedHistoryData?.vehicle_number}</div></div></div>
                              </div>
                              <div className="row g-3">
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block">Start Date &amp; Time</small><div className="fw-bold">{formatDateTimeForDisplay(speedHistoryData?.start_time)}</div></div></div>
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded h-75"><small className="text-muted d-block">End Date &amp; Time</small><div className="fw-bold">{formatDateTimeForDisplay(speedHistoryData?.end_time)}</div></div></div>
                              </div>
                              <div className="row g-3">
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block">Total Distance</small><div className="fw-bold">{speedHistoryData?.total_distance_km} km</div></div></div>
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block">Trip Duration</small><div className="fw-bold">{speedHistoryData?.trip_duration} HH:MM</div></div></div>
                              </div>
                              <div className="row g-3">
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block">Average Speed</small><div className="fw-bold">{speedHistoryData?.average_speed} km/h</div></div></div>
                                <div className="col-6"><div className="summary-item p-3 bg-light rounded mb-3 h-75"><small className="text-muted d-block">Maximum Speed</small><div className="fw-bold">{speedHistoryData?.maximum_speed} km/h</div></div></div>
                              </div>
                              <div className="row g-3">
                                <div className="col-12">
                                  <div className="summary-item p-3 bg-light rounded mb-3 h-75">
                                    {speedHistoryData?.event_type === "rollover" ? (<><div style={{ color: "#6C757D" }}>Event Type: <span className="fw-bold" style={{ color: "#1a7a73" }}>{formatEventType(speedHistoryData?.event_type)}</span></div><div style={{ color: "#6C757D" }}>Date &amp; Time: <span className="fw-bold" style={{ color: "#1a7a73" }}>{formatDateTimeForDisplay(speedHistoryData?.accident_time)}</span></div></>) : speedHistoryData?.event_type === "stable" ? (<><div style={{ color: "#6C757D" }}>Event Type: <span className="fw-bold" style={{ color: "#1a7a73" }}>{formatEventType(speedHistoryData?.event_type)}</span></div><div style={{ color: "#6C757D" }}>Date &amp; Time: <span className="fw-bold" style={{ color: "#1a7a73" }}>N/A</span></div></>) : (<div style={{ color: "#6C757D" }}>No accident detected</div>)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted" style={{ fontSize: 13 }}>No trip summary data available. Run Speed History to load details.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.1); opacity: 0.8; } 100% { transform: scale(1); opacity: 1; } }
        .gm-ui-hover-effect { display: none !important; }
        .gm-style-iw-chr { display: none !important; }
        .gm-style-iw-d { overflow: hidden !important; padding: 0 !important; }
        .gm-style-iw-c { padding: 0 !important; border-radius: 12px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.18) !important; }
        .gm-style-iw { padding: 0 !important; }
        .gm-style .gm-style-iw-t::after { background: linear-gradient(45deg, rgba(255,255,255,1) 50%, rgba(255,255,255,0) 51%, rgba(255,255,255,0) 100%) !important; }
      `}</style>
    </>
  );
};

export default GpsTravelView;